import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy
} from '@angular/core';
import {
  Message,
  UserService,
  MessageTarget,
  EnterpriseSearchService,
  ServiceType,
  WithSubscriptionComponent,
  BasketItem,
  MessagesService
} from '@sabstravtech/obtservices/angular';
import { Subscription } from 'rxjs';
import {
  BookingStatus,
  OBTAirportDetails,
  Country,
  Traveller
} from '@sabstravtech/obtservices/base';
import { Router } from '@angular/router';
import { faUser as faUserRegular } from '@fortawesome/free-regular-svg-icons';
import { faUser as faUserSolid, faCaretLeft } from '@fortawesome/free-solid-svg-icons';

import { LightningUserFavorurite } from '../../classes/user-favourite.enum';

@Component({
  selector: 'app-announcements',
  templateUrl: './announcements.component.html',
  styleUrls: ['./announcements.component.scss'],
  standalone: false
})
export class AnnouncementsComponent extends WithSubscriptionComponent implements OnInit, OnDestroy {
  @Input() public page = '';
  @Input() basket: BasketItem[] = null;
  // giving this in case we are on itin pages
  @Input() basketItemTypes: ServiceType[] = [];
  public loggedInAnnouncements: Message[] = [];
  @Output() udpateAnnCount: EventEmitter<{ annCount: number; loggedInAnnouncements: Message[] }> =
    new EventEmitter<{ annCount: number; loggedInAnnouncements: Message[] }>(true);
  private announcementsSubscription: Subscription;
  private messages: Message[];
  private invokedMessages: Message[];
  private _basketStatus: number;
  public isTravelAdvice = false;
  public loggedInUserOpen = false;

  public invokedUser: Traveller = null;
  public invokedUserOpen = false;
  public invokedAnnouncements: Message[] = [];
  private invokedAnnouncementsSubscription: Subscription;
  public userData: any = null;
  faUserRegular = faUserRegular;
  faUserSolid = faUserSolid;
  faCaretLeft = faCaretLeft;
  public travelAdvice: {
    link: string;
    country: string;
  } = null;

  private currentServiceType: ServiceType;
  private boltSearchResultsServiceTypes: ServiceType[] = [];
  @Input() set basketStatus(value: number) {
    this._basketStatus = value;
    this.filterAnnouncements(this.messages);
    if (this.invokedMessages) {
      this.filterAnnouncements(this.invokedMessages, true);
    }
  }

  get basketStatus(): number {
    return this._basketStatus;
  }

  private _serviceType: ServiceType[] = [];

  @Input() set serviceType(value: ServiceType[]) {
    const previousServiceTypeValue = this._serviceType;
    this._serviceType = value || [];
    if (previousServiceTypeValue?.length !== this._serviceType?.length) {
      this.filterAnnouncements(this.messages);
      if (this.invokedMessages) {
        this.filterAnnouncements(this.invokedMessages, true);
      }
    }
  }
  get serviceType(): ServiceType[] {
    return this._serviceType;
  }

  constructor(
    private readonly userService: UserService,
    private router: Router,
    public searchService: EnterpriseSearchService,
    private messagesService: MessagesService
  ) {
    super();
  }

  ngOnInit(): void {
    // Subscribe to userData observable once
    this.subscribe(this.userService.fullUserDetails.userData, (userData) => {
      this.userData = userData;
    });

    this.subscribe(this.userService.newInvokedUser, (user: Traveller) => {
      this.invokedUser = user;
      if (user) {
        // When invoked user is set, hide logged-in user and show invoked user
        this.loggedInUserOpen = false;
        this.invokedUserOpen = true;
        this.getInvokedUserAnnouncements();
      } else {
        // When invoked user is cleared, show only logged-in user
        this.loggedInUserOpen = false;
        this.invokedUserOpen = false;
        this.invokedAnnouncements = [];
      }
    });

    this.subscribe(this.searchService.searchLoadingDone, (bool: boolean) => {
      this.getAnnouncements();
      if (this.invokedUser) {
        this.getInvokedUserAnnouncements();
      }
      this.setTravelAdvice();
    });

    this.subscribe(
      this.searchService.boltSearchResultsServiceTypes,
      (boltSearchResultsServiceTypes: ServiceType[]) => {
        this.boltSearchResultsServiceTypes = boltSearchResultsServiceTypes;
        this.filterAnnouncementsBolt(this.messages, this.page);
      }
    );

    this.subscribe(
      this.searchService.boltSearchServiceTypes,
      (boltSearchServiceTypes: ServiceType) => {
        this.boltSearchResultsServiceTypes = [boltSearchServiceTypes];
        this.filterAnnouncementsBolt(this.messages, 'search');
      }
    );

    if (this.searchService.searches[ServiceType.Hotel]?.results) {
      this.subscribe(this.searchService.searches[ServiceType.Hotel].results, (results) => {
        const hasRenderedAnnouncements = Object.keys(this.loggedInAnnouncements || {}).length > 0;
        if (
          this.page === 'results' &&
          this.messages?.length &&
          results?.length &&
          !hasRenderedAnnouncements
        ) {
          this.filterAnnouncements(this.messages);
        }
      });
    }
  }

  getAnnouncements() {
    // This always fetches and filters logged-in user's messages into 'announcements'
    this.announcementsSubscription = this.subscribe(
      this.userService.getUserMessages(),
      (messages: Message[]) => {
        this.messages = messages;
        this.filterAnnouncements(messages);
      }
    );
  }

  getInvokedUserAnnouncements() {
    if (this.invokedUser && this.invokedUser.id) {
      this.invokedAnnouncementsSubscription = this.subscribe(
        this.userService.getUserMessages(this.invokedUser.id),
        (messages: Message[]) => {
          this.invokedMessages = messages;
          this.filterAnnouncements(messages, true);
        }
      );
    }
  }

  filterAnnouncements(messages: Message[], isInvoked = false) {
    const messageList: Message[] = [];
    if (messages) {
      messages.forEach((message: Message) => {
        const checkedMessage: Message = this.checkMessage(message);
        switch (this.page.toLowerCase()) {
          case 'search':
            if (message.displayTarget === MessageTarget.SearchForm) {
              if (checkedMessage) {
                messageList.push(checkedMessage);
              }
            }
            break;
          case 'results':
            if (message.displayTarget === MessageTarget.Results) {
              if (checkedMessage) {
                messageList.push(checkedMessage);
              }
            }
            break;
          case 'itinerary':
            if (
              this.basketStatus === BookingStatus.ChooseMIGroup ||
              this.basketStatus === BookingStatus.ProvideMi
            ) {
              if (message.displayTarget === MessageTarget.MiForm) {
                if (checkedMessage) {
                  messageList.push(checkedMessage);
                }
              }
            } else if (
              this.basketStatus === BookingStatus.ProvideBi ||
              this.basketStatus === BookingStatus.ApprovedBook
            ) {
              if (message.displayTarget === MessageTarget.BasketBeforeConfirm) {
                if (checkedMessage) {
                  messageList.push(checkedMessage);
                }
              }
            }
            break;
          default:
            break;
        }
      });

      this.updateAnnouncements(messageList, isInvoked);
    }
  }

  updateAnnouncements(messageList: Message[], isInvoked = false) {
    const groupedMessages = messageList.reduce(function (r: Message, a: Message) {
      r[a.key] = r[a.key] || [];
      r[a.key].push(a);
      return r;
    }, Object.create(null));

    if (isInvoked) {
      this.invokedAnnouncements = groupedMessages;
      // Auto-open invoked user section when messages are loaded
      if (Object.keys(this.invokedAnnouncements).length > 0) {
        this.invokedUserOpen = true;
        this.loggedInUserOpen = false;
      }
    } else {
      this.loggedInAnnouncements = groupedMessages;
      this.countAnn();
    }
  }

  /**
   * @deprecated This method is no longer used.
   * The "Clear All" functionality was removed from the template long ago ENT-10251
   */
  checkClearAllButton(messageList: Message[]): boolean {
    return messageList.some((message: Message) => message.clickToConfirm === true);
  }

  private parseServiceTypeFromURL(url: string, message: any): ServiceType | null {
    const lastPart = url?.toLowerCase();
    switch (lastPart) {
      case 'hotels':
        return ServiceType.Hotel;
      case 'cars':
        return ServiceType.Car;
      case 'flights':
        return ServiceType.Flight;
      case 'rail':
        if (message.key === 'message_rail') {
          return ServiceType.Rail;
        }
        break;
      case 'europeanrail':
        return ServiceType.InternationalRail;
      case 'parkings':
        return ServiceType.Parking;
      case 'lounges':
        return ServiceType.Lounge;
      case 'fast-track':
        return ServiceType.FastTrack;
      case 'ferry':
        return ServiceType.Ferry;
      case 'taxis':
        return ServiceType.Cab;
      case 'eurostar':
        return ServiceType.Eurostar;
      default:
        return null;
    }
  }

  private isServiceTypeRecognizedInURL(message: any, url: string): boolean {
    const serviceType = this.parseServiceTypeFromURL(url, message);
    if (serviceType !== null) {
      this.currentServiceType = serviceType;
      return true;
    }
    return false;
  }

  filterAnnouncementsBolt(messages: Message[], page: string) {
    const messageList: Message[] = [];
    if (messages) {
      messages.forEach((message: Message) => {
        const checkedMessage = this.checkMessageBolt(message);
        switch (page.toLowerCase()) {
          case 'bolt':
            if (message.displayTarget === MessageTarget.Results) {
              if (checkedMessage) {
                messageList.push(checkedMessage);
              }
            }
            break;
          case 'search':
            if (message.displayTarget === MessageTarget.SearchForm) {
              if (checkedMessage) {
                messageList.push(checkedMessage);
              }
            }
            break;
          default:
            break;
        }
      });
      this.updateAnnouncements(messageList);
    }
  }

  checkMessageBolt(message: Message): Message {
    message = this.updateMessageDescription(message);
    const serviceType = this.boltSearchResultsServiceTypes?.find((service) =>
      message.description.toUpperCase().includes(service)
    );
    if (serviceType) {
      return this.messagesService.filterAnnouncementsConfig(
        message,
        serviceType,
        null,
        null,
        true,
        this.userService.userIsAgent()
      )
        ? message
        : null;
    }
  }

  checkMessage(message: Message): Message {
    message = this.updateMessageDescription(message);
    const serviceType = this.serviceType?.find((service) =>
      message.description.toUpperCase().includes(service)
    );
    const url = this.router.url.split('/');
    const lastPart = url[url.length - 1].toLowerCase();
    if (
      this.serviceType.length &&
      ((serviceType && serviceType === this.parseServiceTypeFromURL(lastPart, message)) ||
        (serviceType &&
          this.page === 'search' &&
          this.searchService.search_objects[serviceType]?.chosen) ||
        message.description.toUpperCase().includes('SUPPORT') ||
        message.description.toUpperCase().includes('SYSTEM') ||
        message.description.toUpperCase().includes('GENERIC'))
    ) {
      // return this.filterAnnouncementsConfig(message, serviceType) ? message : null;
      return this.messagesService.filterAnnouncementsConfig(
        message,
        serviceType,
        this.page,
        this.basket,
        false,
        this.userService.userIsAgent()
      )
        ? message
        : null;
    } else if (!this.serviceType.length) {
      if (
        this.basketItemTypes.length &&
        !message.description.toUpperCase().includes('SUPPORT') &&
        !message.description.toUpperCase().includes('SYSTEM') &&
        !message.description.toUpperCase().includes('GENERIC') &&
        this.page === 'itinerary'
      ) {
        const messageServiceType = this.basketItemTypes.find((type) =>
          message.description.toUpperCase().includes(type)
        );
        return !!messageServiceType &&
          this.messagesService.filterAnnouncementsConfig(
            message,
            messageServiceType,
            this.page,
            this.basket,
            false,
            this.userService.userIsAgent()
          )
          ? message
          : null;
      } else if (
        (this.page !== 'search' && !this.isServiceTypeRecognizedInURL(message, lastPart)) ||
        message.description.toUpperCase().includes('SUPPORT') ||
        message.description.toUpperCase().includes('SYSTEM') ||
        message.description.toUpperCase().includes('GENERIC')
      ) {
        return message;
      } else if (this.isServiceTypeRecognizedInURL(message, lastPart)) {
        const serviceType = message.description.toUpperCase().includes(this.currentServiceType);
        if (serviceType) {
          return this.messagesService.filterAnnouncementsConfig(
            message,
            this.currentServiceType,
            this.page,
            this.basket,
            false,
            this.userService.userIsAgent()
          )
            ? message
            : null;
        }
      }
    } else if (this.basketItemTypes.length) {
      const messageServiceType = this.basketItemTypes.find((type) =>
        message.description.toUpperCase().includes(type)
      );
      return !!messageServiceType &&
        this.messagesService.filterAnnouncementsConfig(
          message,
          messageServiceType,
          this.page,
          this.basket,
          false,
          this.userService.userIsAgent()
        )
        ? message
        : null;
    }
  }

  updateMessageDescription(message): Message {
    if (message.description === 'Taxi Message') {
      message.description = 'Cab Message';
    }
    if (message.description === 'IRL Message') {
      message.description = 'INTERNATIONAL_RAIL Message';
    }
    if (message.description === 'Fast Track Message') {
      message.description = 'FAST_TRACK Message';
    }
    if (message.description === 'Long Stays Message') {
      message.description = 'LONG_STAYS Message';
    }
    if (
      message.description === 'Ferry Message' ||
      message.description === 'Ferry/Eurotunnel Message'
    ) {
      message.description = 'FERRY Message';
    }
    if (message.description === 'Season Tickets Message') {
      message.description = 'SEASON_TICKET Message';
    }
    if (message.description === 'Meeting Room Message') {
      message.description = 'MEETING_ROOM Message';
    }

    return message;
  }

  countAnn() {
    let annCount = 0;
    for (const annKey of Object.keys(this.loggedInAnnouncements)) {
      const annPage = this.loggedInAnnouncements[annKey];
      if (!annPage) {
        continue;
      }
      annCount += Object.values(annPage).length;
    }
    if (annCount > 0 && !this.invokedUserOpen) {
      this.loggedInUserOpen = true;
    }
  }

  /**
   * @deprecated This method is no longer used.
   * The "Clear All" functionality was removed from the template long ago ENT-10251
   */
  clearAllAnnouncements() {
    const announcementsList: string[] = [];
    const keys = Object.keys(this.loggedInAnnouncements);
    for (let j = 0; j < keys.length; j++) {
      for (let k = 0; k < this.loggedInAnnouncements[keys[j]].length; k++) {
        if (this.loggedInAnnouncements[keys[j]][k].clickToConfirm) {
          announcementsList.push(this.loggedInAnnouncements[keys[j]][k].id);
        }
      }
    }
    this.deleteAnnouncement(announcementsList);
  }

  deleteAnnouncement(id: string | string[]) {
    this.subscribe(this.userService.confirmMessages(id), (confirmed: boolean) => {
      if (confirmed) {
        this.getAnnouncements();
      } else {
        throw new Error("Can't confirm the message");
      }
    });
  }

  setTravelAdvice() {
    const travelAdviceFlag: any = this.userService.getUserFavoriteObject(
      LightningUserFavorurite.DisplayFCDOAdvice
    );
    if (
      this.router.url.includes('/results/flights') &&
      travelAdviceFlag &&
      travelAdviceFlag.value
    ) {
      const searchParams = this.searchService.searches[ServiceType.Flight];
      const destination = searchParams.arriveLocation as OBTAirportDetails;
      const country = this.formatCountry(destination);
      if (destination.country !== 'United Kingdom') {
        this.isTravelAdvice = true;
        this.travelAdvice = {
          country: destination.country,
          link: `https://www.gov.uk/foreign-travel-advice/${country}`
        };
        this.loggedInUserOpen = true;
      }
    } else if (
      this.router.url.includes('/results/hotels') &&
      travelAdviceFlag &&
      travelAdviceFlag.value
    ) {
      const searchParams = this.searchService.searches[ServiceType.Hotel];
      const country = searchParams.country;
      const countryName = this.formatCountryHotels(country);
      if (country.cCode !== 'GB') {
        this.isTravelAdvice = true;
        this.travelAdvice = {
          country: countryName,
          link: `https://www.gov.uk/foreign-travel-advice/${countryName.replaceAll(' ', '-').toLowerCase()}`
        };
      }
    }
  }

  formatCountry(destination: OBTAirportDetails) {
    const countryList = this.searchService.getCountries();
    const currentCountry = countryList.find((country) => country.cCode === destination.countryCode);
    let countryName =
      currentCountry?.cName === destination.country ? currentCountry?.cName : destination.country;
    if (currentCountry?.altName) {
      countryName = currentCountry?.altName;
    }

    return countryName.replaceAll(' ', '-').toLowerCase();
  }

  formatCountryHotels(country: Country) {
    const countryList = this.searchService.getCountries();
    const currentCountry = countryList.find((listCountry) => listCountry.cCode === country.cCode);
    let countryName = currentCountry?.cName;
    if (currentCountry?.altName) {
      countryName = currentCountry?.altName;
    }

    return countryName;
  }

  toggleLoggedInUserSection(): void {
    this.loggedInUserOpen = !this.loggedInUserOpen;
  }

  toggleInvokedUserSection(): void {
    this.invokedUserOpen = !this.invokedUserOpen;
  }

  ngOnDestroy(): void {
    if (this.announcementsSubscription) {
      this.announcementsSubscription.unsubscribe();
    }
    if (this.invokedAnnouncementsSubscription) {
      this.invokedAnnouncementsSubscription.unsubscribe();
    }
    // Since announcements are no longer in a popup we don't need to trap focus
    // this.removeFocusTrap();
  }

  async suppressAnnouncement(id: string): Promise<void> {
    await this.userService.confirmMessages(id).toPromise();
  }
}
